import styled from "styled-components";
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { SignHeader } from "components/HeaderTitleComponent";
import { ClassInfo, provinceInfo, SignContextProps, SignType, SingleResult, UserInfo } from "type/type";
import { aesCBCDecrypt, aesCBCEncrypt, checkDateAndFormattedDate, checkFutureDate, checkMobile, checkName, formattedSelectOption, ROLE_DIRECTOR, ROLE_TEACHER, signTitleText } from "utils/commonUtils";
import { checkDuplicateUserId, checkExistPhoneNumber, getSignClass, getSmsAuthRequest, getSmsAuthVerify, postSignUser } from "api/api";
import { FlexWrapC, FlexWrapR, InputWrapC, InputWrapR } from "components/common/CommonLayout";
import { ShortModalComponent, ShortModalPropsInterface } from "components/ModalComponent";
import { SolidPrimaryBtn, SolidPrimarySubBtn } from "components/button/Button";
import { CommonInput, CustomCheckbox, SelectOption } from "components/input/Input";
import { useNavigate } from "react-router-dom";
import { BearSVG, DinosaurSVG, GrassSVG } from "styles/global-styles";

const MainWrap = styled.main`
    width: 100%;
    padding: 3.75rem 4.375rem 2.5rem;
    background-color: ${({theme}) => theme.newTheme.text.btnText_w};
`;
const MainSection = styled.section`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
    > article {
        &:first-of-type {
            background-color: ${({theme}) => theme.newTheme.default.secondary};
            padding: 0.75rem 1rem;
            border-radius: 0.5rem;

            div {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.5rem;
                color: ${({theme}) => theme.newTheme.text.mainText};
                font-size: 1rem;
                font-weight: 600;
            }
            p {
                padding-left: 2rem;
                line-height: 1.125rem;
            }
            :hover:not(p) {
                cursor: pointer;
            }
        }
    }

    label {
        font-weight: 600;
    }
`;

const AgreementArticle = styled.article`
    width: 100%;
    min-height: 3.25rem;
    padding: 0 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;

    button {
        margin-left: auto;
        &:hover {
            opacity: 0.5;
        }
    }
    &:hover,
    label:hover {
        cursor: pointer;
    }
`;

const WelcomePage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 1rem auto;
    gap: 1rem;
    text-align: center;

    div:first-of-type p {
        font-size: 2.125rem;
        color: ${({theme}) => theme.newTheme.default.primary};
        font-family: var(--font-nanumSquare-acB);
    }
    div:last-of-type p {
        font-size: 1.125rem;
    }
`;
const ImageWrap = styled.div`
    position: relative;
    height: 260px;
    margin-top: auto;
    svg {
        position: absolute;
    }
    .image-1 {
        width: 178px;
        transform: rotateY(180deg);
        bottom: 60px;
        left: -90px;
    }
    .image-2 {
        width: 125px;
        bottom: 0;
        left: 90px;
    }
    .image-3 {
        width: 205px;
        bottom: 0;
        left: 160px;
    }
    .image-4 {
        width: 236px;
        bottom: 60px;
        right: -70px;
    }
`;

// 가입 시 필요한 기본 정보
const defaultSignData: SignType = {
    user: {
        user_site_list:[{
            site_id: 0,
            approval: 0
        }],
        name: "",
        email: "",
        phone: "",
        role: "",
        login_id: "",
        login_pw: "",
        temp_phone: "",
        terms_agree: 0,
        unique_code: "",
        resident_number: "",
        gender: 0,
        card_no: "",
        class_id: 0,
        student_id: 0,
        temp_mobile_os: 0, // web 해당 없음
        temp_mobile_token:"", // web 해당 없음
        base64_list: []
    },
    site: {
        name: "",
        address: "",
        phone: "",
        fax: "",
        email: "",
    },
    site_id: 0,
    class_id: 0
}

// 가입 시 입력하는 내용 저장용
export const SignContext = createContext<SignContextProps>({
    signData: defaultSignData,
    setSignData: () => {}
});

function SignProvider({ children }: { children: ReactNode }) {

    // sessionStorage에서 데이터를 가져와 초기 상태를 설정
    const [signData, setSignData] = useState<SignType>(() => {
        const savedData = sessionStorage.getItem('sessionData');
        return savedData ? JSON.parse(savedData) : defaultSignData;
    });
    // 상태가 변경될 때마다 sessionStorage에 데이터를 저장
    useEffect(() => {
        sessionStorage.setItem('sessionData', JSON.stringify(signData));
    }, [signData]);

    // 회원가입 페이지가 변경될 때 sessionStorage가 비어있으면 회원가입 첫 페이지(sign)으로 이동
    return (
        <SignContext.Provider value={{ signData, setSignData }}>{children}</SignContext.Provider>
    )
}

interface SignProps {
    title1: string,
    title2: string,
    step: number,
    data: SignType
}
interface StepComponentProps {
    data: SignType,
    setStepProps: React.Dispatch<React.SetStateAction<SignProps>>;
}

/**
 * 회원가입 페이지를 감싸고 있는 component
 * @returns 
 */
export function Sign() {

    const signContext = useContext(SignContext);
    if (!signContext) {
        throw new Error("SignContext must be used within a SignProvider");
    }

    const { signData } = signContext;

    // 회원가입 step
    const [stepProps, setStepProps] = useState<SignProps>({
        title1: "",
        title2: "",
        step: 0,
        data: signData
    });

    useEffect(() => {
        if (stepProps.step === 99) return; // welcome 페이지

        setStepProps(prev => ({
            ...prev,
            title1: signTitleText[prev.step].title1,
            title2: signTitleText[prev.step].title2,
        }));
    }, [stepProps.step]);

    return (
        <SignProvider>
            <MainWrap>
                {stepProps.step === 99 ?
                    <WelcomeComponent data={stepProps.data} setStepProps={setStepProps}/>
                    :
                    <>
                        <SignHeader
                            title1={stepProps.title1}
                            title2={stepProps.title2}
                            page={stepProps.step + 1}
                        />
                        {stepProps.step === 0
                            ? <Step1PolicyComponent data={stepProps.data} setStepProps={setStepProps} />
                            : stepProps.step === 1
                                ? <Step2PolicyComponent data={stepProps.data} setStepProps={setStepProps} />
                                : stepProps.step === 2
                                    ? <Step3PolicyComponent data={stepProps.data} setStepProps={setStepProps} />
                                    : stepProps.step === 3
                                        ? <Step4PolicyComponent data={stepProps.data} setStepProps={setStepProps} />
                                        : stepProps.step === 4
                                            ? <Step5PolicyComponent data={stepProps.data} setStepProps={setStepProps} />
                                            : null}
                    </>
                }
            </MainWrap>
        </SignProvider>
    );
}

// 필수 약관 동의 페이지
type AgreeType = {
    required: boolean; // 필수/선택
    name: string; // input id값용
    isChecked: boolean; // 체크 여부
    text: string; // 약관동의 text
};

// 필수 약관 동의 기본 데이터
const initialCheckData: AgreeType[] = [
    { required: true, name: "term", isChecked: false, text: "아이스쿨(AI SCHOOL) 이용약관 동의" },
    { required: true, name: "privacy", isChecked: false, text: "개인정보 수집 및 이용 동의" },
    { required: true, name: "location", isChecked: false, text: "위치정보 이용동의 및 위치기반 서비스 이용약관 동의" },
    // { required: true, name: "sensitive", isChecked: false, text: "민감정보 수집 및 이용 동의" },
    // { required: true, name: "thirdParty", isChecked: false, text: "개인정보 제3자 제공 동의" },
    // { required: false, name: "marketing", isChecked: false, text: "마케팅 정보 수신 동의" },
];

const Step1PolicyComponent: React.FC<StepComponentProps> = ({ data, setStepProps }) => {
    
    const handleNextStep = () => {

        setStepProps((prev) => ({
            ...prev,
            step: prev.step + 1, // 다음 단계로 이동
        }));
    };

    // 필수 약관 동의 체크
    const [checkData, setCheckData] = useState<AgreeType[]>(initialCheckData);
    const [checkCount, setCheckCount] = useState(0);
    const [isCheckAll, setIsCheckAll] = useState(false);
    // 선택 약관 동의
    const [optionalChecked, setOptionalChecked] = useState(false);
    

    // isRequired: 1 필수 2 선택
    const handleOnchange = (event: React.ChangeEvent<HTMLInputElement>, isRequired: boolean, index: number) => {
    
        const { checked } = event.target;

        // 필수 약관이면서 동의 체크한 경우
        if (isRequired && checked) setCheckCount((prev) => prev + 1);
        else if (isRequired && !checked) {
            setCheckCount((prev) => prev - 1);
            setIsCheckAll(false);
        }
        // 선택 약관이면서 동의 체크한 경우 - 마케팅 정보 수신 동의
        if (!isRequired) setOptionalChecked(checked);

        setCheckData((prevData) => {
            const newData = [...prevData];
            newData[index] = { ...newData[index], isChecked: checked };
            return newData;
        });
    };

    // 필수 약관 모두 동의 시
    const checkAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setIsCheckAll(checked);
        setCheckCount(checked ? checkData.length : 0);
    };


    // 이용약관 버튼
    function PolicyButton({ policyName }: { policyName: string }) {

        const openPolicy = () => {
            window.open(`/${policyName}.html`, '_blank');
        }

        return (
            <button className="policy-btn" onClick={openPolicy}>
            <img src="../image/icon/arrow_right.svg" alt="arrow_right" />
            </button>
        );
    }

    useEffect(() => {
        // 모두 동의를 클릭한 경우
        if (isCheckAll) {
          let newData = checkData.map((d) => ({ ...d, isChecked: true }));
          setCheckData(newData);
        } else {
          // 모두 동의를 해제인 경우
          if (checkCount === 0) {
            let newData = checkData.map((d) => ({ ...d, isChecked: false }));
            setCheckData(newData);
          }
        }
      }, [isCheckAll, checkCount]);

    return (
        <>
            <MainSection>
                {/* 해당 페이지에 대한 내용 */}
                <article>
                    <div>
                        <CustomCheckbox
                            type="checkbox"
                            id="agreeAll"
                            checked={checkCount === checkData.length}
                            onChange={(e) => checkAll(e)}
                        />
                        <label htmlFor="agreeAll" style={{width: '100%'}}>모두 동의</label>
                    </div>
                    {/* <p>
                        전체 동의는 필수 및 선택정보에 대한 동의도 포함되어 있으며,
                        개별적으로도 동의를 선택하실 수 있습니다. 선택항목에 대한 동의를
                        거부하시는 경우에도 서비스는 이용이 가능합니다.
                    </p> */}
                </article>
                <div>
                    {checkData.map((data, index) => 
                        <AgreementArticle>
                            <CustomCheckbox
                                type="checkbox"
                                id={data.name}
                                name={data.name}
                                checked={data.isChecked}
                                onChange={(e) => handleOnchange(e, data.required, index)}
                            />
                            <label htmlFor={data.name}>{`(${data.required ? '필수' : '선택'})${data.text}`}</label>
                            <PolicyButton policyName={data.name} />
                        </AgreementArticle>
                    )}
                </div>
            </MainSection>
            <SolidPrimaryBtn type="button" disabled={checkCount !== checkData.length} onClick={() => handleNextStep()}>
                동의
            </SolidPrimaryBtn>
        </>
    )
}

type ConfirmType = {
    name: string;
    mobile: string;
    resident_number: string;
    gender: number;
};
  
const Step2PolicyComponent: React.FC<StepComponentProps> = ({ data, setStepProps }) => {

    const genderRef = useRef<HTMLInputElement>(null);
  
    // required 체크
    const [isRequired, setIsRequired] = useState(false);
    // 입력값
    const [userData, setUserData] = useState<SignType>(data);
    
    const [isNameOk, setIsNameOk] = useState(true);
    const [nameErrorMsg, setNameErrorMsg] = useState("");
    const [isMobileOk, setIsMobileOk] = useState(true);
    const [mobileErrorMsg, setMobileErrorMsg] = useState("");
  
    function updateConfirm(updates: Partial<ConfirmType>) {
        setUserData({
            ...userData,
            user: {
                ...userData.user,
                ...updates
            }
      });
    }
    
    function handleOnChange(e: any) {
        const { name, value } = e.target;

        validateUserForm(name, value);
        updateConfirm({ [name]: value });
    }

    /** 휴대폰 번호 입력 */
    function handlePhoneChange(e: any) {
        const { name } = e.target;
        
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 11) {
            validateUserForm("phone", value);
            updateConfirm({ [name]: value });
        }
            
    }
    /** 생년월일 입력 */
    function handleResidentChange(e: any) {
      const value = e.target.value.replace(/\D/g, ''); // 숫자만 허용
      if (value.length <= 6) {
        updateConfirm({ resident_number: value });
      }
    }
  
    /** 성별 입력 */
    function handleGenderChange(e: any) {
      const value = e.target.value.replace(/\D/g, '');
      if (value.length <= 1) {
        updateConfirm({ gender: Number(value) });
      }
    }
  
    async function handleNextStep() {
        const inputBirth = userData.user.resident_number;
        // 유효한 생년월일인지 체크
        const birthDay = inputBirth.slice(0, 2) + '-' + inputBirth.slice(2, 4) + '-' + inputBirth.slice(4);
  
        // 말일 체크
        const currentYYYY = new Date().getFullYear(); // 현재 년도
        const inputYY = inputBirth.slice(0, 2); // 작성한 년도(YY)
        const yyyy = Number(String(currentYYYY - 100).slice(2) >= inputYY ? `20${inputYY}` : `19${inputYY}`); // 작성한 년도(YY)의 앞자리
        const lastDay = new Date(yyyy, Number(inputBirth.slice(2, 4)), 0).getDate(); // 작성년/월의 말일

        if (lastDay < Number(inputBirth.slice(4))) {
            alert("올바른 생년월일을 입력해주세요.");
            return;
        }
        
        const formatDay = checkDateAndFormattedDate(birthDay);
        const isPastDay = checkFutureDate(formatDay) !== 'P'; // true면 return

        if (isPastDay) {
            alert("올바른 생년월일을 입력해주세요.");
            return;
        }
        
        // 성별(주민등록번호 뒷자리 1번째) 1 ~ 8: 2099년까지는 0, 9는 사용X
        if (userData.user.gender === 0 || userData.user.gender === 9) {
            alert("올바른 생년월일을 입력해주세요.");
            return;
        }
      
        // 중복된 휴대폰 번호인지 체크
        const isExistPhone = await checkExistPhoneNumber(aesCBCEncrypt(userData.user.phone));
  
        if (isExistPhone.code === 200) {
            setStepProps((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    user: {
                        ...prev.data.user,
                        name: aesCBCEncrypt(userData.user.name),
                        phone: aesCBCEncrypt(userData.user.phone),
                        resident_number: aesCBCEncrypt(userData.user.resident_number),
                        gender: userData.user.gender,
                        base64_list:[],
                    },
                },
                step: prev.step + 1
            }));
        }
        else {
            alert("이미 가입되어 있는 번호입니다.");
            return;
        }

    }
  
    // enter키
    function handleActiveEnter(e: any) {
        if (e.key === 'Enter') {
    
            if (isNameOk && isMobileOk) {
                handleNextStep();
            }
        }
    }
  
    function validateUserForm(type: string, value: string) {
        if (type === "name") {
            let result = checkName(value);
            value === ""
                ? setNameErrorMsg("이름을 입력해주세요.")
                : setNameErrorMsg("정확한 이름을 입력해주세요.");
            setIsNameOk(result);
        }
        else if (type === "phone") {
            let result = checkMobile(value);
            value === ""
                ? setMobileErrorMsg("휴대폰 번호를 입력해주세요.")
                : setMobileErrorMsg("정확한 휴대폰 번호를 입력해주세요.");
            setIsMobileOk(result);
        }
    }
  
    // 생년월일 6자리 입력 시 자동으로 성별 input focus
    useEffect(() => {
        if (userData.user.resident_number.length === 6) {
            if(genderRef.current) genderRef.current.focus();
        }
    }, [userData.user.resident_number]);
  
    useEffect(() => {
        if (!userData.user.name || !userData.user.phone || !userData.user.resident_number || !userData.user.gender) setIsRequired(false);
        else if (userData.user.name !== "" && userData.user.phone !== "") {
            if (isNameOk && isMobileOk) setIsRequired(true);
            else setIsRequired(false);
        } else setIsRequired(false);
    }, [userData, isNameOk, isMobileOk]);

    return (
        <>
            <MainSection>
                <InputWrapC $justifyContent="center">
                    <label htmlFor="name">이름</label>
                    <CommonInput
                        type="text"
                        id="name"
                        className={isNameOk ? "" : "error"}
                        name="name"
                        value={userData.user.name}
                        onChange={handleOnChange}
                        placeholder="이름 입력"
                        required
                    />
                    <p className={isNameOk ? "none" : "show"}>{nameErrorMsg}</p>
                </InputWrapC>
                <InputWrapC $justifyContent="center">
                    <label htmlFor="resident_number">생년월일/성별</label>
                    <InputWrapR $alignItems="center" $justifyContent="space-between" $gap="0.25rem">
                        <CommonInput
                            type="text"
                            id="resident_number"
                            name="resident_number"
                            value={userData.user.resident_number}
                            maxLength={6}
                            onChange={handleResidentChange}
                            placeholder="생년월일 입력"
                            required
                            style={{width: "100%"}}
                        />
                        <span style={{margin: "0 0.5rem"}}>-</span>
                        <CommonInput
                            ref={genderRef}
                            type="text"
                            id="gender"
                            name="gender"
                            maxLength={1}
                            value={userData.user.gender || ""}
                            onChange={handleGenderChange}
                            required
                            style={{width: "3rem"}}
                        />
                        <CommonInput type="text" value="●●●●●●" disabled />
                    </InputWrapR>
                </InputWrapC>
                <InputWrapC $justifyContent="center">
                    <label htmlFor="phone">휴대폰 번호</label>
                    <CommonInput
                        type="text"
                        id="phone"
                        className={isMobileOk ? "" : "error"}
                        name="phone"
                        value={userData.user.phone}
                        onChange={handlePhoneChange}
                        onKeyDown={handleActiveEnter}
                        placeholder="휴대폰 번호 입력(숫자만 입력)"
                        maxLength={11}
                        required
                    />
                    <p className={isMobileOk ? "none" : "show"}>{mobileErrorMsg}</p>
                </InputWrapC>
            </MainSection>
            <SolidPrimaryBtn type="button" disabled={!isRequired} onClick={() => handleNextStep()}>
                인증번호 받기
            </SolidPrimaryBtn>
        </>
    );
}

/**
 * SMS 인증 페이지
 * @param param0 
 * @returns 
 */
const Step3PolicyComponent: React.FC<StepComponentProps> = ({ data, setStepProps }) => {

    // ************************************ prop
  
    // 인증시간
    const MINUTES_IN_MS = 3 * 60 * 1000;
    const INTERVAL = 1000;
    const [leftTime, setLeftTime] = useState<number>(MINUTES_IN_MS);
    const [isFail, setIsFail] = useState(false);
  
    const mins = String(Math.floor((leftTime / (1000 * 60)) % 60)).padStart(2, "0");
    const sec = String(Math.floor((leftTime / 1000) % 60)).padStart(2, "0");
    
    // ************************************ state
  
    // 받은 인증번호
    const [, setAuthNumber] = useState("");
    // 입력한 인증번호
    const [authVerifyNumber, setAuthVerifyNumber] = useState("");
  
    //모달 상태
    const [modalProps, setModalProps] = useState<ShortModalPropsInterface>({
      isOpen: false,
      isClose: () => {},
      title: "",
      content: null,
    });
  
    // ************************************ handle
  
    // 인증번호 입력
    function handleOnChange(e: any) {
        const { value } = e.target;
        if (isNaN(value)) setAuthVerifyNumber(authVerifyNumber);
        else setAuthVerifyNumber(value);
    }

    // enter키
    function handleActiveEnter(e: any) {
        if (e.key === 'Enter') {
            handleNextStep();
        }
    }
  
    // 인증번호 다시 받기 요청하기
    function getSmsVerify() {

        setAuthVerifyNumber(""); // input 초기화
        setLeftTime(MINUTES_IN_MS); // 3분 초기화
        setIsFail(false);
        smsRequest(); // SMS인증 재요청

    }
    
    // 인증완료 버튼 클릭
    async function handleNextStep() {

        const result = await getSmsAuthVerify(data.user.phone, authVerifyNumber);
        
        // 인증이 완료되면 페이지 이동
        if (result.code === 200) {
            setStepProps((prev) => ({
                ...prev,
                step: prev.step + 1, // 다음 단계로 이동
            }));
        }
        else if (result.code === 400) {
            alert("인증번호가 일치하지 않습니다.");
            return;
        }
        else {
            alert(result.message);
            return;
        }
    }
  
    // 인증번호 요청
    const smsRequest = async () => {
  
        // 이름/핸드폰번호/생년월일/성별 모두 있어야 인증번호 전송
            if (data.user.name && data.user.phone && data.user.resident_number && data.user.gender) {
            
                const result: SingleResult<string> = await getSmsAuthRequest({
                    name: aesCBCDecrypt(data.user.name),
                    phone: data.user.phone,
                    resident_number: data.user.resident_number,
                    gender: data.user.gender
                });
    
            if (result.code === 200) setAuthNumber(result.data);
            else {
                alert(result.message);
                setAuthNumber("");
                return;
            }
        }
    };
  
    //모달 열기 버튼 클릭 시
    const openModal = () => {
        let title = "인증번호가 오지 않나요?";
        let content:JSX.Element = <></>;
    
        setModalProps({
            isOpen: true,
            isClose: () => setModalProps({...modalProps, isOpen: false}),
            title,
            content: content,
        });
    }

    //인증번호 모달 컨텐츠
    const shortModalContent = (): JSX.Element => {
  
        return (
            <>
            <div style={{borderBottom: '1px solid #DEDEDE', paddingBottom: '0.5rem'}}>
                <p>아래 사유로 인증 번호가 오지 않을수도 있습니다. 확인 후 다시 시도해주세요.</p>
            </div>
            <article>
                <p className="sub-title">스팸 문자 거부</p>
                <p style={{ whiteSpace: 'pre-wrap'}}>스팸거부앱 혹은 통신사에서 031-689-3380를 스팸차단 대상 번호에서 해제하거나, 스팸거부앱 자체를 일시적으로 끄는 것을 권장합니다.</p>
            </article>
            </>
        )
    }
  
    // INTERVAL 마다 실행
    useEffect(() => {
        const timer = setInterval(() => {
            setLeftTime((prev) => prev - INTERVAL);
        }, INTERVAL);
        if (leftTime <= 0) {
            clearInterval(timer);
            setIsFail(true);
        }
        return () => {
            clearInterval(timer);
        };
    }, [leftTime]);

    useEffect(() => {
        smsRequest();
    }, []);
  
    return (
        <>
            <MainSection style={{gap: '2rem'}}>
                <FlexWrapR $gap="1rem" $justifyContent="space-between">
                    <FlexWrapC style={{width: '80%'}}>
                        <label htmlFor="verifyNumber">인증번호</label>
                        <CommonInput
                            type="text"
                            id="verifyNumber"
                            className={isFail ? "error" : ""}
                            name="verifyNumber"
                            value={authVerifyNumber}
                            onChange={handleOnChange}
                            onKeyDown={handleActiveEnter}
                            placeholder="인증번호 입력"
                            required
                        />
                        {isFail && <p>인증번호를 다시 받아주세요.</p>}
                    </FlexWrapC>
                    <p className="text-red">{mins} : {sec}</p>
                </FlexWrapR>
                <SolidPrimarySubBtn type="button" onClick={getSmsVerify} style={{width: '10rem'}}>
                    인증번호 다시 받기
                </SolidPrimarySubBtn>
            </MainSection>
            <FlexWrapC $gap="1rem" $alignItems="center" style={{marginTop: 'auto'}}>
                <p className="tip" onClick={openModal}>인증번호가 오지 않나요?</p>
                <SolidPrimaryBtn type="button" disabled={!authVerifyNumber} onClick={() => handleNextStep()}>
                    인증완료
                </SolidPrimaryBtn>
            </FlexWrapC>

            <ShortModalComponent
                                isOpen={modalProps.isOpen}
                                isClose={modalProps.isClose}
                                content={shortModalContent()}
                                title={modalProps.title}
            />
        </>
    );
}

/**
 * 아이디/패스워드/패스워드확인
 */
type AccountReqType = {
    id: string, password: string, passwordCheck: string,
}

/**
 * 계정 생성 페이지
 * @param param0 
 * @returns 
 */
const Step4PolicyComponent: React.FC<StepComponentProps> = ({ data, setStepProps }) => {

    // required 체크
    const [isRequired, setIsRequired] = useState(false);

    // 입력값
    const [inputData, setInputData] = useState<AccountReqType>({
        id: "", password: "", passwordCheck: "",
    });
    const [isIdOk, setIsIdOk] = useState(true);
    const [idErrorMsg, setIdErrorMsg] = useState("");
    const [isPasswordOk, setIsPasswordOk] = useState(true);
    const [passwordErrorMsg, setPasswordErrorMsg] = useState("");

    // 비밀번호 입력값 보이기/가리기
    const [eyeOnOff1, setEyeOnOff1] = useState(false);
    const [eyeOnOff2, setEyeOnOff2] = useState(false);

    // ************************************ handle

    async function handleNextStep() {

        if (!inputData.id) {
            setIsIdOk(false);
            setIdErrorMsg("아이디를 입력해주세요.");
            return;
        }
        else if (!inputData.password || !inputData.passwordCheck) {
            setPasswordErrorMsg("비밀번호를 입력해주세요.");
            return;
        }
        else if (inputData.password !== inputData.passwordCheck) {
            setPasswordErrorMsg("비밀번호를 확인해주세요.");
            return;
        }
        else {
            setIsIdOk(true);
            setIdErrorMsg("");
        }

        // 아이디 중복 체크
        const result = await checkDuplicateUserId();
        
        if (result.code === 200) {
            const ids:string[] = result.data.map((user: UserInfo) => user.login_id);
            const isExist = ids.find(id => inputData.id === id) ? true : false;

            if (!isExist) {
                setStepProps((prev) => ({
                    ...prev,
                    data: {
                        ...prev.data,
                        user: {
                            ...prev.data.user,
                            login_id: inputData.id,
                            login_pw: aesCBCEncrypt(inputData.password),
                            role: ROLE_TEACHER, // 학교
                        },
                        site_id: 1 // 설치형은 1 고정
                    },
                    step: prev.step + 1
                }));
            }
            else {
                alert('아이디를 다시 입력해주세요.');
                return;
            }
        }
        else {
            alert(result.message);
            return;
        }

    }
    
    // 비밀번호 input type 변경
    function toggleOnOff1() {
        setEyeOnOff1(prev => !prev);
    }
    function toggleOnOff2() {
        setEyeOnOff2(prev => !prev);
    }
    
    const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (name === "id" && value === "") {
            setIsIdOk(false);
            setIdErrorMsg("아이디를 입력해주세요.");
        }
        else if (name === "id" && !(/^[a-zA-Z0-9]+$/g).test(value)) {
            setIsIdOk(false);
            setIdErrorMsg("정확한 아이디를 입력해주세요.");
        }
        else if ((name === "password" || name === "passwordCheck") && value === "") {
            setPasswordErrorMsg("비밀번호를 입력해주세요.");
        }
        else {
            setIsIdOk(true);
            setIdErrorMsg("");
        }
        setInputData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // enter키
    function handleActiveEnter(e: any) {
        if (e.key === 'Enter') {
            handleNextStep();
        }
    }
    
    // 비밀번호 / 비밀번호 재입력 일치 여부
    useEffect(() => {
        if (inputData.passwordCheck !== inputData.password) {
            setIsPasswordOk(false);
            setPasswordErrorMsg("비밀번호가 일치하지 않습니다.");
        }
        else {
            setIsPasswordOk(true);
            setPasswordErrorMsg("");
        }
    }, [inputData.password, inputData.passwordCheck]);

    // 빈값 유효성 검사
    useEffect(() => {
        if (inputData.id === "" || inputData.password === "") setIsRequired(false);
        else if (inputData.id !== "" && inputData.password !== "") {
            if (isIdOk && isPasswordOk) setIsRequired(true);
            else setIsRequired(false);
        }
        else setIsRequired(false);
    }, [inputData, isIdOk, isPasswordOk]);

    return (
        <>
            <MainSection>
                <InputWrapC>
                    <label htmlFor="id">아이디</label>
                    <CommonInput type="text" id="id" className={isIdOk ? "" : "error"} name="id" value={inputData.id}
                        onChange={handleChangeValue}
                        onKeyDown={handleActiveEnter}
                        placeholder="아이디 입력"
                        required />
                    <p className={isIdOk ? "none" : "show"}>{idErrorMsg}</p>
                </InputWrapC>
                <InputWrapC>
                    <label htmlFor="password">비밀번호</label>
                    <input type={!eyeOnOff1 ? 'password' : 'text'} id="password"
                        value={inputData.password} name="password"
                        onChange={handleChangeValue}
                        onKeyDown={handleActiveEnter}
                        placeholder='비밀번호 입력' />
                    <div className='eye-on-off' onClick={toggleOnOff1}>
                        {!eyeOnOff1 ? <img src="/image/icon/eye_visible.svg" alt="eye_visible" /> : <img src="/image/icon/eye_invisible.svg" alt="eye_invisible" />}
                    </div>
                </InputWrapC>
                <InputWrapC>
                    <label htmlFor="passwordCheck">비밀번호 재입력</label>
                    <input
                        type={!eyeOnOff2 ? 'password' : 'text'}
                        id="passwordCheck"
                        className={isPasswordOk ? "" : "error"}
                        value={inputData.passwordCheck}
                        name="passwordCheck"
                        onChange={handleChangeValue}
                        onKeyDown={handleActiveEnter}
                        placeholder='비밀번호 재입력' />
                    <div className='eye-on-off' onClick={toggleOnOff2}>
                        {!eyeOnOff2 ? <img src="/image/icon/eye_visible.svg" alt="eye_visible" /> : <img src="/image/icon/eye_invisible.svg" alt="eye_invisible" />}
                    </div>
                    <p className={isPasswordOk ? "none" : "show"}>{passwordErrorMsg}</p>
                </InputWrapC>
            </MainSection>
            <SolidPrimaryBtn type="button" disabled={!isRequired} onClick={() => handleNextStep()}>다음</SolidPrimaryBtn>
        </>
    );
}

const Step5PolicyComponent: React.FC<StepComponentProps> = ({ data, setStepProps }) => {

    const navigate = useNavigate();
    // 반 선택 select-option
    const [selectOptionClass, setSelectOptionClass] = useState<provinceInfo[]>([
        { label: "반 미정", value: 0 },
    ]);

    // 선택한 업장에 있는 반 데이터 가져오기
    async function getApiData() {

        try {
            const result = await getSignClass(data.site_id);

            if (result.code === 200) {

                const array: any[]
                    = formattedSelectOption(result.data.map((d: ClassInfo) => ({ label: d.name, value: d.id })), '반 미정');
                
                setSelectOptionClass(array);

            } else {
                alert(result.message);
                return;
            }
        } catch (error) {
            console.error("API error ", error);
        }
    }

    // 반 선택
    function handleChangeOption(prop: number) {
        setStepProps((prev) => ({
            ...prev,
            data: {
                ...prev.data,
                class_id: prop,
            }
        }));
    }

    async function handleNextStep() {

        if (!data.site_id) {
            navigate("/sign");
            return;
        }

        // 회원가입이 정상적으로 진행되면 welcome 페이지로 이동
        const result = await postSignUser(data, data.site_id);

        if (result.code === 200) {
            setStepProps((prev) => ({
                ...prev,
                step: 99, // welcome으로 이동
            }));
        }
        else {
            const goToLogin = window.confirm(
                `${result.message} 처음으로 돌아가시겠습니까?`
            );
            if (goToLogin) navigate("/sign");
            return;
        }
    }

    const RegisterCenter = () => {

        return (
            <></>
        )
    }

    useEffect(() => {
        getApiData();
    }, []);

    return(
        data.user.role === ROLE_TEACHER ? 
            <>
                <MainSection>
                    <InputWrapC>
                        <label>담당 반</label>
                        <SelectOption
                            optionData={selectOptionClass}
                            onChange={(prop: number) => handleChangeOption(prop)}
                        />
                    </InputWrapC>
                </MainSection>
                <SolidPrimaryBtn type="button" onClick={() => handleNextStep()}>
                    다음
                </SolidPrimaryBtn>
            </>
         : <RegisterCenter />
    )
}
 
// 환영 페이지
const WelcomeComponent: React.FC<StepComponentProps> = ({ data, setStepProps }) => {

    const navigate = useNavigate();

    function goToLoginPage() {
        sessionStorage.removeItem("sessionData");
        navigate("/login");
    }

    return (
        <>
            <WelcomePage>
                <div>
                    <p>
                        {aesCBCDecrypt(data.user.name) }
                        {data.user.role === ROLE_DIRECTOR ? "관리자"
                            : data.user.role === ROLE_TEACHER ? "선생님" : "학부모님"}
                    </p>
                    <p>가입을 환영합니다!</p>
                </div>
                <div>
                    <p>사랑스러운 우리 아이들의 학교 생활을</p>
                    <p>편리하게 관리해 보세요!</p>
                </div>
            </WelcomePage>
            <ImageWrap>
                <GrassSVG className="image-1" />
                <GrassSVG className="image-4" />
                <BearSVG className="image-3" />
                <DinosaurSVG className="image-2" />
            </ImageWrap>
            <SolidPrimaryBtn type="button" onClick={() => goToLoginPage()}>
                로그인하기
            </SolidPrimaryBtn>
        </>
    );
  }